import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { beforeAfterResults, APPLY_URL } from '../mock';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export const BeforeAfter3D = ({ onApplyClick }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <section ref={ref} className="bg-panel py-24 md:py-32 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-20 space-y-6"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.p 
            className="font-accent text-xl md:text-2xl italic text-gold"
            initial={{ opacity: 0, y: -50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Visual Proof
          </motion.p>
          <motion.h2 
            className="font-hero text-3xl md:text-5xl lg:text-6xl uppercase tracking-wide text-cream"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            style={{ fontWeight: 700 }}
          >
            Before & After
          </motion.h2>
          <motion.p
            className="font-body text-base md:text-lg text-[#e8e6df]/80 max-w-2xl mx-auto font-light"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Driven individuals who applied structure and systems to their training. No shortcuts — real results.
          </motion.p>
        </motion.div>

        {/* 3D Transformation Cards */}
        <div className="space-y-16">
          {beforeAfterResults.map((result, index) => (
            <TransformationCard key={index} result={result} index={index} inView={inView} />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 100 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          <motion.div
            whileHover={{ scale: 1.05, rotateX: 5 }}
            whileTap={{ scale: 0.95 }}
            style={{ perspective: '1000px' }}
          >
            <Button
              onClick={() => window.open(APPLY_URL, '_blank', 'noopener,noreferrer')}
              size="lg"
              className="bg-[#c6a04a] hover:bg-[#d9be7e] text-[#0d0d0d] font-semibold text-sm md:text-lg px-6 md:px-12 py-5 md:py-7 rounded-none uppercase tracking-wider transition-all duration-300 shadow-2xl shadow-[#c6a04a]/50 whitespace-normal h-auto"
            >
              Apply for Your<br className="md:hidden" /> Strategy Call
              <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const TransformationCard = ({ result, index, inView }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -20;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60, rotateY: index % 2 === 0 ? -20 : 20 }}
      animate={inView ? { opacity: 1, x: 0, rotateY: 0 } : {}}
      transition={{ 
        delay: index * 0.2,
        duration: 1,
        type: "spring",
        stiffness: 80
      }}
      style={{ perspective: '2000px' }}
    >
      <motion.div
        className={`grid ${result.singleImage ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-8 bg-dark border-2 border-[#c6a04a]/30 p-8 md:p-12 cursor-pointer`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={() => setIsFlipped(!isFlipped)}
        animate={{
          rotateX: mousePosition.y * 0.5,
          rotateY: mousePosition.x * 0.5,
        }}
        whileHover={{ 
          scale: 1.02,
          borderColor: 'rgba(198, 160, 74, 0.8)',
          boxShadow: '0 30px 80px rgba(198, 160, 74, 0.4)'
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{
          transformStyle: 'preserve-3d',
        }}
      >
        {result.singleImage ? (
          /* Single Image Display */
          <motion.div 
            className="relative overflow-hidden group max-w-md mx-auto w-full"
            style={{ transform: 'translateZ(50px)' }}
            whileHover={{ scale: 1.05 }}
          >
            <div className="aspect-[3/4] relative">
              <img
                src={result.before}
                alt={result.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-transparent to-transparent opacity-60" />
              <div className="absolute bottom-4 left-4">
                <p className="font-display text-xl uppercase tracking-wide text-gold">Transformation</p>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
            {/* Before Image */}
            <motion.div 
              className="relative overflow-hidden group"
              style={{ transform: 'translateZ(50px)' }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="aspect-[3/4] relative">
                <img
                  src={result.before}
                  alt={`${result.name} before`}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-transparent to-transparent opacity-60" />
                <div className="absolute bottom-4 left-4">
                  <p className="font-display text-xl uppercase tracking-wide text-gold">Before</p>
                </div>
              </div>
            </motion.div>

            {/* After Image */}
            <motion.div 
              className="relative overflow-hidden group"
              style={{ transform: 'translateZ(50px)' }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="aspect-[3/4] relative">
                <img
                  src={result.after}
                  alt={`${result.name} after`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-transparent to-transparent opacity-60" />
                <div className="absolute bottom-4 left-4">
                  <p className="font-display text-xl uppercase tracking-wide text-gold">After</p>
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* Info Overlay */}
        <motion.div 
          className={`${result.singleImage ? '' : 'md:col-span-2'} text-center mt-6`}
          style={{ transform: 'translateZ(75px)' }}
        >
          <motion.div
            className="inline-block bg-[#c6a04a]/20 border border-[#c6a04a] px-8 py-4"
            whileHover={{ scale: 1.05, translateZ: 20 }}
          >
            <p className="font-display text-2xl md:text-3xl uppercase tracking-wide text-gold mb-2">
              {result.result}
            </p>
            <p className="font-display text-lg md:text-xl uppercase tracking-wide text-cream mb-1">
              {result.name}
            </p>
            <p className="font-body text-sm text-gold-soft">
              {result.title}
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};
